import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import t from '@guestyci/localize/t.macro';
import {
  Text,
  Heading,
  Stack,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  createSchema,
  required,
} from '@guestyci/arc';
import { OtpInput } from '../../components/InputOTP';
import { Mail } from '@guestyci/icons/system';
import { FormLayout, StepActions } from '../layout';
import RememberDeviceCheckbox from './RememberDeviceCheckbox';
import ResendCodeButton from './ResendCodeButton';

const codeFormSchema = createSchema({
  code: required(),
});

const CustomEmailMFA = ({
  userEmail,
  isVerifyMode,
  onSubmitCode,
  onResendCode,
  onPickDifferentMethod,
  error,
  isSubmitting,
}) => {
  const [rememberDevice, setRememberDevice] = useState(false);

  const form = useForm({
    resolver: zodResolver(codeFormSchema),
    defaultValues: { code: '' },
    mode: 'onSubmit',
  });

  const handleSubmit = (data) => {
    onSubmitCode(data.code.trim(), rememberDevice);
  };

  const secondaryLabel = isVerifyMode
    ? t('Back')
    : t('Pick a different authentication method');

  return (
    <FormLayout>
      <Stack spacing={6} justify="between" className="gst-w-full gst-flex-1">
        <Stack spacing={6}>
          <Stack align="center" justify="center" className="gst-rounded-full gst-bg-success-foreground gst-w-20 gst-h-20 gst-shrink-0">
            <Mail className="gst-size-6 gst-text-primary" aria-hidden="true" />
          </Stack>

          <Stack spacing={0}>
            <Heading variant="h2" className="gst-tracking-tight">
              {t('Email authentication sent to')}
            </Heading>
            {userEmail && (
              <Heading variant="h2" className="gst-text-primary gst-tracking-tight">
                {userEmail}
              </Heading>
            )}
          </Stack>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} noValidate>
              <Stack spacing={6}>
                <FormField
                  control={form.control}
                  name="code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('Enter the code you received')}</FormLabel>
                      <FormControl>
                        <OtpInput
                          value={field.value}
                          onChange={field.onChange}
                          disabled={isSubmitting}
                        />
                      </FormControl>
                      <FormMessage />
                      {error && !form.formState.errors.code && (
                        <Text type="error" size="sm">
                          {error}
                        </Text>
                      )}
                    </FormItem>
                  )}
                />

                <ResendCodeButton onResend={onResendCode} disabled={isSubmitting} />

                <RememberDeviceCheckbox
                  id="email-mfa-remember-device"
                  checked={rememberDevice}
                  onCheckedChange={setRememberDevice}
                  disabled={isSubmitting}
                />

                <StepActions
                  primaryLabel={t('Verify code')}
                  primaryType="submit"
                  onSecondaryClick={onPickDifferentMethod}
                  secondaryLabel={secondaryLabel}
                  isSubmitting={isSubmitting}
                />
              </Stack>
            </form>
          </Form>
        </Stack>
      </Stack>
    </FormLayout>
  );
};

export default CustomEmailMFA;
